import * as THREE from 'three';
import type { Cell } from '../puzzle';
import { cellRect, type BoardRect, type GridArg } from '../play/layout';

export type Segment = { cell: Cell; side: 'l' | 'r' | 't' | 'b' };

export function boundarySegments(cells: Cell[]): Segment[] {
  const key = (r: number, c: number) => `${r},${c}`;
  const set = new Set(cells.map((c) => key(c.row, c.col)));
  const out: Segment[] = [];
  for (const cell of cells) {
    if (!set.has(key(cell.row, cell.col - 1))) out.push({ cell, side: 'l' });
    if (!set.has(key(cell.row, cell.col + 1))) out.push({ cell, side: 'r' });
    if (!set.has(key(cell.row - 1, cell.col))) out.push({ cell, side: 't' });
    if (!set.has(key(cell.row + 1, cell.col))) out.push({ cell, side: 'b' });
  }
  return out;
}

export function outlineMesh(
  rect: BoardRect,
  grid: GridArg,
  cells: Cell[],
  width: number,
  color: number,
): THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial> {
  const positions: number[] = [];
  const index: number[] = [];
  const h = width / 2;
  const segs = boundarySegments(cells);
  const has = (cell: Cell, side: Segment['side']) => segs.some((s) => s.cell === cell && s.side === side);
  for (const { cell, side } of segs) {
    const r = cellRect(rect, grid, cell);
    // 横边向两端各伸出 h 盖住角；竖边在本格有横边的那一端缩进 h，避免角上重叠混合两次
    const vy0 = r.y0 + (has(cell, 'b') ? h : 0);
    const vy1 = r.y1 - (has(cell, 't') ? h : 0);
    // 横边仅在本格同端有竖边时才伸出 h，避免相邻格接缝处重叠
    const hx0 = r.x0 - (has(cell, 'l') ? h : 0);
    const hx1 = r.x1 + (has(cell, 'r') ? h : 0);
    const [x0, y0, x1, y1] =
      side === 'l' ? [r.x0 - h, vy0, r.x0 + h, vy1]
      : side === 'r' ? [r.x1 - h, vy0, r.x1 + h, vy1]
      : side === 't' ? [hx0, r.y1 - h, hx1, r.y1 + h]
      : [hx0, r.y0 - h, hx1, r.y0 + h];
    const base = positions.length / 3;
    positions.push(x0, y0, 0, x1, y0, 0, x1, y1, 0, x0, y1, 0);
    index.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(index);
  const mesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color, transparent: true, depthWrite: false }));
  mesh.frustumCulled = false;
  return mesh;
}
